import { useSyncExternalStore } from 'react';
import type { OfflineOwnerSnapshot } from './offline-owner-reading';
import { readRememberedSession, sdkSessionStorageKey } from './offline-reopen-session';

export interface ReadingAccessSnapshot {
  restricted: boolean;
  ownerId: string | null;
  local: OfflineOwnerSnapshot | null;
  epoch: number;
}
const listeners = new Set<() => void>();
let admission: { token: symbol; ownerId: string; current: () => boolean } | null = null;
let local: { value: OfflineOwnerSnapshot; current: () => boolean } | null = null;
let snapshot: ReadingAccessSnapshot = {
  // Standalone persistence probes have no mounted app gate. Browser callers
  // start closed, including before the first React effect.
  restricted: typeof window !== 'undefined', ownerId: null, local: null, epoch: 0,
};
function refresh() {
  if (admission && !admission.current()) admission = null;
  if (local && admission?.ownerId !== local.value.ownerId && !local.current()) local = null;
  const restricted = typeof window !== 'undefined' && admission === null;
  const ownerId = admission?.ownerId ?? local?.value.ownerId ?? null;
  const visible = local && (!admission || admission.ownerId === local.value.ownerId) ? local.value : null;
  if (snapshot.restricted !== restricted || snapshot.ownerId !== ownerId || snapshot.local !== visible) {
    snapshot = { restricted, ownerId, local: visible, epoch: snapshot.epoch + 1 };
  }
}
function emit() { refresh(); for (const listener of listeners) listener(); }
export function getReadingAccessSnapshot() { refresh(); return snapshot; }
export function isReadingRestricted() { return getReadingAccessSnapshot().restricted; }
export function subscribeReadingAccess(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}
export function useReadingAccess() {
  return useSyncExternalStore(subscribeReadingAccess, getReadingAccessSnapshot, getReadingAccessSnapshot);
}
/** Continuity permits public BSB reading, never admission to owner runtimes. */
export function hasRememberedReadingSession(): boolean {
  const projectUrl = import.meta.env?.VITE_SUPABASE_URL;
  if (!projectUrl || typeof window === 'undefined') return false;
  try {
    const key = sdkSessionStorageKey(projectUrl);
    return readRememberedSession(() => localStorage.getItem(key), projectUrl).kind === 'present';
  } catch {
    return false;
  }
}
/** Only the already-successful owner gate may call this; no storage grant. */
export function setReadingAdmission(ownerId: string, current: () => boolean = () => true) {
  const token = Symbol('reading-admission');
  admission = { token, ownerId, current };
  emit();
  return () => {
    if (admission?.token !== token) return;
    admission = null;
    emit();
  };
}
/** A readonly snapshot is never authority to mount the owner runtime. */
export function setLocalReadingSnapshot(value: OfflineOwnerSnapshot | null, current: () => boolean = () => false) {
  local = value ? { value, current } : null;
  emit();
}

function subscribeConnection(listener: () => void) {
  window.addEventListener('online', listener);
  window.addEventListener('offline', listener);
  return () => {
    window.removeEventListener('online', listener);
    window.removeEventListener('offline', listener);
  };
}
export function useDefinitelyOffline() {
  return useSyncExternalStore(subscribeConnection,
    () => navigator.onLine === false, () => false);
}
